import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ShieldWarning, ArrowsClockwise, Scales } from "@phosphor-icons/react";

/**
 * Phase 10.2E — Eligibility panel (rule-based).
 * Shows the current eligibility verdict on the CRM lead + admin override controls.
 * READ-ONLY on customer's WhatsApp; never sends messages.
 */
export default function EligibilityPanel({ lead, onChange }) {
  const [busy, setBusy] = useState(false);
  const [audit, setAudit] = useState([]);

  const e = lead?.eligibility || null;

  const loadAudit = () => {
    if (!lead?.id) return;
    api.get(`/admin/crm/leads/${lead.id}/eligibility-audit`)
      .then(r => setAudit(r.data?.history || []))
      .catch(() => {});
  };
  useEffect(loadAudit, [lead?.id]);

  const runClassify = () => {
    if (!lead?.id) return;
    setBusy(true);
    api.post(`/admin/crm/leads/${lead.id}/classify-eligibility`)
      .then(r => {
        const v = r.data?.eligibility;
        toast.success(`Eligibility: ${v?.status}${v?.reason ? ` (${v.reason})` : ""}`);
        onChange?.();
        loadAudit();
      })
      .catch(err => toast.error(err?.response?.data?.detail || "Classify failed"))
      .finally(() => setBusy(false));
  };

  const override = (status) => {
    if (!lead?.id) return;
    const reason = prompt(
      status === "eligible"
        ? "Reason for marking Eligible (Admin note):"
        : status === "not_eligible"
        ? "Reason for marking Not Eligible:"
        : "Reason for marking Needs Review:",
      ""
    );
    if (reason === null) return;
    setBusy(true);
    api.post(`/admin/crm/leads/${lead.id}/eligibility-override`, {
      status,
      reason: reason || "admin_override",
    })
      .then(r => {
        toast.success(`Marked ${status} by admin`);
        onChange?.();
        loadAudit();
      })
      .catch(err => toast.error(err?.response?.data?.detail || "Override failed"))
      .finally(() => setBusy(false));
  };

  const tone = eligibilityTone(e?.status);

  return (
    <div data-testid="eligibility-panel"
         className={`rounded-md border p-4 ${tone.wrap}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2">
            {e?.status === "not_eligible"
              ? <ShieldWarning size={16} weight="bold" className={tone.icon} />
              : <ShieldCheck size={16} weight="bold" className={tone.icon} />}
            <div className={`text-[11px] tracking-widest uppercase font-bold ${tone.icon}`}>
              Eligibility
            </div>
            <span className="text-[9px] text-white/40 uppercase tracking-widest">Rule-based · No AI</span>
          </div>
          <div className="text-[10px] text-white/40 mt-1">
            Deterministic rules (km &gt; 100k, year &lt; 2016, commercial/yellow plate). Never sends messages.
          </div>
        </div>
        <button
          type="button"
          onClick={runClassify}
          disabled={busy}
          data-testid="eligibility-run"
          className="text-[10px] tracking-widest uppercase font-bold py-2 px-3 border border-blue-500 text-blue-300 hover:bg-blue-500/10 disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          <ArrowsClockwise size={12} weight="bold" />
          {busy ? "Running…" : "Run Classification"}
        </button>
      </div>

      {!e && (
        <div data-testid="eligibility-empty" className="text-[11px] text-white/50 italic">
          No classification yet. Click <span className={tone.icon}>Run Classification</span>.
        </div>
      )}

      {e && (
        <div data-testid="eligibility-result" className="space-y-2">
          <div className="flex flex-wrap gap-2 text-[10px]">
            <Chip label="Status"  value={e.status}  testid="eligibility-status" tone={tone.chip} />
            {e.reason && <Chip label="Reason" value={e.reason} testid="eligibility-reason" tone="neutral" />}
            {e.source_field && <Chip label="Triggered by" value={e.source_field} tone="neutral" />}
            {e.override && <Chip label="Admin override" value="yes" tone="warn" />}
            {e.rule_version && <Chip label="Rule" value={e.rule_version} tone="neutral" />}
          </div>

          {e.notes && (
            <div data-testid="eligibility-notes" className="text-[11px] text-white/80 border-l-2 border-white/20 pl-2">
              {e.notes}
            </div>
          )}

          {e.trigger_value != null && (
            <div className="text-[11px] text-white/60">
              <span className="text-[9px] uppercase tracking-widest text-white/40 mr-1">Trigger value:</span>
              <span className="font-mono">{String(e.trigger_value)}</span>
            </div>
          )}

          <div className="pt-2 border-t border-[#1a1a1a] flex flex-wrap gap-2">
            <button type="button" onClick={() => override("eligible")} disabled={busy}
                    data-testid="eligibility-override-eligible"
                    className="text-[10px] tracking-widest uppercase font-bold py-1.5 px-2.5 border border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50">
              Mark Eligible
            </button>
            <button type="button" onClick={() => override("needs_admin_review")} disabled={busy}
                    data-testid="eligibility-override-review"
                    className="text-[10px] tracking-widest uppercase font-bold py-1.5 px-2.5 border border-amber-500/50 text-amber-300 hover:bg-amber-500/10 disabled:opacity-50">
              Needs Review
            </button>
            <button type="button" onClick={() => override("not_eligible")} disabled={busy}
                    data-testid="eligibility-override-not-eligible"
                    className="text-[10px] tracking-widest uppercase font-bold py-1.5 px-2.5 border border-red-500/50 text-red-300 hover:bg-red-500/10 disabled:opacity-50">
              Not Eligible
            </button>
          </div>

          <div className="text-[9px] text-white/30 uppercase tracking-widest pt-1">
            Last checked: {e.checked_at || "—"} · Triggered: {e.triggered_by || "—"} · History: {audit.length} run{audit.length === 1 ? "" : "s"}
          </div>

          {audit.length > 0 && (
            <details data-testid="eligibility-audit-details" className="text-[11px]">
              <summary className="text-white/40 uppercase tracking-widest text-[9px] cursor-pointer">Audit trail ({audit.length})</summary>
              <div className="mt-1 space-y-1 max-h-64 overflow-y-auto">
                {audit.slice(0, 20).map(row => (
                  <div key={row.id || row.generated_at} className="text-[10px] border-l-2 border-white/10 pl-2 py-1">
                    <div className="flex justify-between text-white/50">
                      <span>{row.triggered_by} · {row.verdict?.status}</span>
                      <span>{row.generated_at}</span>
                    </div>
                    {row.verdict?.notes && <div className="text-white/60">{row.verdict.notes}</div>}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function Chip({ label, value, tone = "neutral", ...rest }) {
  const tones = {
    good:    "border-emerald-500 text-emerald-300 bg-emerald-500/10",
    danger:  "border-red-500 text-red-300 bg-red-500/10",
    warn:    "border-amber-500 text-amber-300 bg-amber-500/10",
    neutral: "border-[#2A2A2A] text-white/70 bg-white/5",
  };
  return (
    <span {...rest} className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border rounded-sm ${tones[tone] || tones.neutral}`}>
      <span className="text-white/40 mr-1">{label}:</span>
      {value || "—"}
    </span>
  );
}

function eligibilityTone(status) {
  if (status === "eligible") {
    return { wrap: "border-emerald-500/40 bg-emerald-500/5", icon: "text-emerald-300", chip: "good" };
  }
  if (status === "not_eligible") {
    return { wrap: "border-red-500/40 bg-red-500/5", icon: "text-red-300", chip: "danger" };
  }
  if (status === "needs_admin_review") {
    return { wrap: "border-amber-500/40 bg-amber-500/5", icon: "text-amber-300", chip: "warn" };
  }
  return { wrap: "border-[#2A2A2A] bg-[#0D0D0D]/70", icon: "text-white/60", chip: "neutral" };
}
